import { useState, useEffect, useRef } from 'react';
import api, { handleApiError } from '@/services/api';

export interface LastReply {
  id: string;
  date: string;
  subject: string;
  from: {
    name: string;
    email: string;
  };
  uid: number;
  flags: string[];
  body_text: string;
}

export interface EmailMessage {
  id: string;
  uid: number;
  replyCount: number;
  seqno: number;
  flags: string[];
  size: number;
  isReply: boolean;
  lastReply?: LastReply;
  mainEmail?: {
    id: string;
    uid: number;
    message_id: string;
    subject: string;
    folder: {
      id: string;
      name: string;
    };
  };
  envelope: {
    from: Array<{ name: string; mailbox: string; host: string }>;
    to: Array<{ name: string; mailbox: string; host: string }>;
    subject: string;
    date: string;
  };
  header?: any;
  body?: {
    html: string;
    text: string;
    raw: string;
  };
  attachments?: Attachment[];
}

export interface Attachment {
  filename: string;
  contentType: string;
  size: number;
  contentDisposition: string;
  contentId: string;
  content: string;
  part_id: string;
}

export interface EmailResponse {
  messages: EmailMessage[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  folder: {
    name: string;
    path: string;
    totalMessages: number;
    unreadMessages: number;
    newMessages: number;
  };
}

export interface EmailsOptions {
  sortBy?: 'date' | 'subject' | 'from';
  sortOrder?: 'asc' | 'desc';
  search?: string;
  action?: 'refresh' | 'sync';
  all?: boolean;
  unread?: boolean;
}

// Custom event for sync completion
export const SYNC_COMPLETE_EVENT = 'sync-complete';

const CACHE_TTL_MS = 60_000;

export const useEmails = (
  folderName: string = 'INBOX', 
  page: number = 1, 
  limit: number = 20, 
  category?: string, 
  refreshKey?: number,
  options: EmailsOptions = {}
) => {
  const [emails, setEmails] = useState<EmailMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<EmailResponse['pagination'] | null>(null);
  const [folder, setFolder] = useState<EmailResponse['folder'] | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [shouldRetry, setShouldRetry] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);
  const requestIdRef = useRef<string>('');
  const lastRefreshKeyRef = useRef(refreshKey ?? 0);

  // LocalStorage key for this folder/page
  const localKey = `emails_${folderName}_${page}`;

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  type CachedEmailPayload = {
    emails: EmailMessage[];
    pagination: EmailResponse['pagination'];
    folder: EmailResponse['folder'];
    timestamp?: number;
  };

  const loadFromCache = (): { data: CachedEmailPayload; isFresh: boolean } | null => {
    const cached = localStorage.getItem(localKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached) as CachedEmailPayload;
        if (parsed.emails?.length > 0) {
          const age = Date.now() - (parsed.timestamp || 0);
          return { data: parsed, isFresh: age < CACHE_TTL_MS };
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    return null;
  };

  // Main effect for fetching emails
  useEffect(() => {
    const fetchEmails = async () => {
      if (!folderName || !isMountedRef.current) return;

      const refreshKeyChanged = (refreshKey ?? 0) !== lastRefreshKeyRef.current;
      if (refreshKeyChanged) {
        lastRefreshKeyRef.current = refreshKey ?? 0;
      }

      const isForcedRefresh = refreshKeyChanged || options.action === 'sync';
      const currentRequestId = `${folderName}_${page}_${category ?? ''}_${refreshKey ?? 0}_${retryCount}_${Date.now()}`;
      requestIdRef.current = currentRequestId;
      
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      
      const cachedResult = !isForcedRefresh ? loadFromCache() : null;
      const hasCachedData = Boolean(cachedResult);

      if (cachedResult) {
        setEmails(cachedResult.data.emails);
        setPagination(cachedResult.data.pagination);
        setFolder(cachedResult.data.folder);
      }

      if (cachedResult?.isFresh && !isForcedRefresh) {
        setLoading(false);
        setError(null);
        return;
      }

      if (!hasCachedData || isForcedRefresh) {
        setLoading(true);
      }
      setError(null);
      
      if (refreshKeyChanged) {
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith(`emails_${folderName}_`)) {
            localStorage.removeItem(key);
          }
        });
      }
      
      try {
        const params = new URLSearchParams({
          includeBody: 'false',
          page: page.toString(),
          limit: limit.toString()
        });
        
        if (category) {
          params.append('category', category);
        }

        if (options.sortBy) {
          params.append('sortBy', options.sortBy);
        }
        if (options.sortOrder) {
          params.append('sortOrder', options.sortOrder);
        }

        if (options.search) {
          params.append('search', options.search);
        }
        if (options.action === 'sync') {
          params.append('forceSync', 'true');
          if (options.all) {
            params.append('all', 'true');
          }
          setLoading(true);
          setIsSyncing(true);
        }

        if (options.unread !== undefined) {
          params.append('isUnread', options.unread.toString());
        }
        
        const response = await api.get<EmailResponse>(`/api/folders/${folderName}?${params.toString()}`, {
          signal: abortControllerRef.current.signal
        });

        if (!isMountedRef.current || requestIdRef.current !== currentRequestId) {
          return;
        }

        if (response.headers['x-mailbox-status'] === 'syncing') {
          setIsSyncing(true);
          setLoading(true);
          setTimeout(() => {
            if (isMountedRef.current) {
              setRetryCount(prev => prev + 1);
            }
          }, 5000);
          return;
        }

        setEmails(response.data.messages);
        setPagination(response.data.pagination);
        setFolder(response.data.folder);
        setRetryCount(0);
        setShouldRetry(false);

        if (options?.action === 'sync' as EmailsOptions['action']) {
          window.dispatchEvent(new CustomEvent(SYNC_COMPLETE_EVENT, {
            detail: {
              folderName,
              success: true
            }
          }));
        }

        setIsSyncing(false);
        setLoading(false);
        
        if (!options.action || options.action === 'refresh') {
          localStorage.setItem(localKey, JSON.stringify({
            emails: response.data.messages,
            pagination: response.data.pagination,
            folder: response.data.folder,
            timestamp: Date.now()
          }));
        }
      } catch (err: any) {
        if (!isMountedRef.current || requestIdRef.current !== currentRequestId) {
          return;
        }
        
        if (err.name === 'AbortError') {
          return;
        }
        
        const errorMessage = handleApiError(err);
        setError(errorMessage);

        if ((err.isTimeout || err.code === 'ECONNABORTED') && isSyncing) {
          setError('Your mailbox is still syncing. This may take a few minutes for large folders. Please try again shortly.');
        }
        
        if ((err.isTimeout || err.isNetworkError || err.code === 'ECONNABORTED') && retryCount < 3) {
          setShouldRetry(true);
        } else {
          setRetryCount(0);
          setShouldRetry(false);
        }
      } finally {
        if (isMountedRef.current && requestIdRef.current === currentRequestId && !isSyncing) {
          setLoading(false);
        }
      }
    };

    fetchEmails();
  }, [
    folderName,
    page,
    limit,
    category,
    refreshKey,
    retryCount,
    options.sortBy,
    options.sortOrder,
    options.search,
    options.action,
    options.unread,
  ]);

  useEffect(() => {
    if (shouldRetry && retryCount < 3) {
      const timeoutId = setTimeout(() => {
        if (isMountedRef.current) {
          setRetryCount(prev => prev + 1);
        }
      }, Math.pow(2, retryCount) * 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [shouldRetry, retryCount]);

  const refreshEmails = () => {
    if (!isMountedRef.current) return;
    setEmails([]);
    setPagination(null);
    setFolder(null);
    setRetryCount(0);
    setShouldRetry(false);
  };

  const retry = () => {
    if (!isMountedRef.current) return;
    setRetryCount(0);
    setError(null);
    setShouldRetry(false);
  };

  return {
    emails,
    loading,
    error,
    pagination,
    folder,
    refreshEmails,
    retry,
    retryCount,
    isSyncing
  };
};
